import type { SocketType } from "../../types";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { configEntryList, prettify } from "./socketDefinition";
import {
  resolveKeyedInputSockets,
  type ResolvedKeyedInputSockets,
} from "./keyedInputSockets";

/**
 * Input sockets for the entity_resolver step, from its configured data inputs,
 * plus the socket id each key maps to. Also used by `web_browser`, whose data
 * inputs have the same shape.
 */
export type ResolvedEntityResolverInputSockets = ResolvedKeyedInputSockets;

/**
 * The two step types that share this generator.
 *
 * The caller has to say which, because only `entity_resolver` is opted into
 * version 2 — `webBrowserHandler` still reads its inputs by key. Hardcoding
 * either would decide the scheme for the other: a web_browser config carrying a
 * stamp would get `key::dynamic` sockets its handler never reads, and those
 * values would vanish with nothing raised.
 */
export type EntityResolverInputStepType = "entity_resolver" | "web_browser";

export function resolveEntityResolverInputSockets(
  config: Record<string, unknown>,
  stepType: EntityResolverInputStepType = "entity_resolver",
): ResolvedEntityResolverInputSockets {
  const dataInputs = configEntryList<{
    key?: unknown;
    label?: unknown;
    list?: unknown;
  }>(config.dataInputs);

  const usable = dataInputs.filter(
    (d): d is { key: string; label?: unknown; list?: unknown } =>
      typeof d.key === "string" && d.key.length > 0,
  );

  // Keyed on the step type, because this generator is shared with
  // `web_browser`, whose handler still reads its inputs by key; see
  // resolveKeyedInputSockets for how the type decides the id scheme.
  return resolveKeyedInputSockets(
    stepType,
    config,
    usable.map((d) => {
      const label =
        typeof d.label === "string" && d.label.length > 0
          ? d.label
          : prettify(d.key);
      return {
        key: d.key,
        socket: {
          name: label,
          // A list input is array-typed so a wired list arrives whole —
          // a single-value socket would fan the step out once per item.
          type: (d.list === true ? "array<json>" : "any") as SocketType,
          required: false,
          description: `Data input: ${label}`,
        },
      };
    }),
  );
}

export function generateEntityResolverInputSockets(
  config: Record<string, unknown>,
  stepType: EntityResolverInputStepType = "entity_resolver",
): DynamicSocketDefinition[] {
  return resolveEntityResolverInputSockets(config, stepType).sockets;
}
